import { createAttributeSet } from "global/Application";
import { Acronym, ArrayToArrayLookupByPredicate, ArrayToLookupByPredicate } from "global/Utility/TSFunctions";
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    IGroupNumber as FAM_IGroupNumber,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import { v4 as uuidv4 } from 'uuid';
import { BaseJsonTransformer } from "./IJsonTransformer";
import { $oset_$claims_ar_claim_recovery_percentage_$30_of_recoveries_id, $oset_$claims_ar_claim_recovery_percentage_$other_enter_percentage_of_recovery_id, $oset_$shared_savings_cap_type_$per_claim_id, $oset_$shared_savings_category_$other_id, $oset_$shared_savings_ss_pi_component_other_$eligibility_over_payment_retroactive_term_id } from "model/ConfigOptionSetConstants";
import { $att_$accounts_receivable_$bcbs_claimrecoverypercentage_id, $att_$accounts_receivable_$bcbs_enterpercentageofrecoveryretrotermclaim_id, $att_$fees_and_premiums_$bcbs_sharedsavings_billtogroupcurrency_id, $att_$fees_and_premiums_$bcbs_sharedsavings_billtogrouppercent_id, $att_$fees_and_premiums_$bcbs_sharedsavings_capamount_id, $att_$fees_and_premiums_$bcbs_sharedsavings_captypecode_id, $att_$fees_and_premiums_$bcbs_sharedsavings_categorycode_id, $att_$fees_and_premiums_$bcbs_sharedsavings_id, $att_$fees_and_premiums_$bcbs_sharedsavings_natokeeppercent_id, $att_$fees_and_premiums_$bcbs_sharedsavings_partnerplanpercent_id, $att_$fees_and_premiums_$bcbs_sharedsavings_sspicomponentcode_83606da3585eee11be6f000d3a3bec27_id } from "model/ConfigAttributeConstants";

/**
 * 
 * Retroactive Termination Claims Recoveries
 * Transformer:
 * In a similar manner as transforming the Network Shared Savings, we need to transform the first snapshot to map from the AR to the Fees and Premiums.
 * 
 * https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/449109
 */
export class Sprint24Transformer extends BaseJsonTransformer {
    config: FAM_IGroupConfigurationConfig;
    json: FAM_IGroupConfigurationData;
    addGeneralNotification: boolean;
    plansByFeesAndPremiumsRefId: Record<string, FAM_IPlan[]>;
    groupNumbersByPlanRefId: Record<string, FAM_IGroupNumber[]>;
    accountsReceivablesById: Record<string, FAM_IAccountsReceivable[]>;


    constructor({ config, json }: { config: FAM_IGroupConfigurationConfig, json: FAM_IGroupConfigurationData }) {
        super();
        this.config = config;
        this.json = json;
        this.addGeneralNotification = false;

        this.plansByFeesAndPremiumsRefId = ArrayToArrayLookupByPredicate(this.json.plans, t => t.feesAndPremiumsRef?.id ?? "N/A");
        this.groupNumbersByPlanRefId = ArrayToArrayLookupByPredicate(this.json.groupStructure.groupNumbers, t => t.planRef?.id ?? "N/A");
        this.accountsReceivablesById = ArrayToArrayLookupByPredicate(this.json.claimsARs, t => t.id);

    }

    async before(json: FAM_IGroupConfigurationData): Promise<void> {
        console.debug("[tran] Sprint24Transformer.before")

        if (!this.json.notifications)
            this.json.notifications = [];
    }

    async transformFeesAndPremium(feesAndPremium: FAM_IFeesAndPremiums): Promise<boolean | void> {

        const bcbs_claimrecoverypercentage_30percent = {
            "type": "entity",
            "entityType": "bcbs_config_optionsetitem",
            "id": $oset_$claims_ar_claim_recovery_percentage_$30_of_recoveries_id,
            "name": "30% of Recoveries"
        };

        const bcbs_claimrecoverypercentage_other = {
            "type": "entity",
            "entityType": "bcbs_config_optionsetitem",
            "id": $oset_$claims_ar_claim_recovery_percentage_$other_enter_percentage_of_recovery_id,
            "name": "Other (Enter Percentage of Recovery)"
        }

        const feesAndPremiumFieldMap = ArrayToLookupByPredicate(feesAndPremium.fields, (t) => {
            return t.configRef.id
        });

        const bcbs_sharedsavings = feesAndPremiumFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_id];

        const bcbs_sharedsavings_config_attribute = this.config.cache.map_bcbs_config_attribute[bcbs_sharedsavings.configRef.id];
        const bcbs_sharedsavings_config_masterattribute = this.config.cache.map_bcbs_config_masterattribute[bcbs_sharedsavings_config_attribute._bcbs_config_masterattributeid_value!];

        const addAttributeSet = (percent: number | undefined, name: string) => {

            const newAttributeSet = createAttributeSet({ config: this.config, json: this.json }, bcbs_sharedsavings_config_masterattribute);

            if (name)
                newAttributeSet.name = name;

            const newAttributeSetFieldMap = ArrayToLookupByPredicate(newAttributeSet.fields, (t) => {
                return t.configRef.id
            });

            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=7b606da3-585e-ee11-be6f-000d3a3bec27
            const bcbs_sharedsavings_categorycode = newAttributeSetFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_categorycode_id];

            // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=83606da3-585e-ee11-be6f-000d3a3bec27
            const bcbs_sharedsavings_sspicomponentcode = newAttributeSetFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_sspicomponentcode_83606da3585eee11be6f000d3a3bec27_id];

            // Bill to Group
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=85606da3-585e-ee11-be6f-000d3a3bec27
            const bcbs_sharedsavings_billtogrouppercent = newAttributeSetFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_billtogrouppercent_id];

            // Bill to Group ($) - (Ignore)
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=87606da3-585e-ee11-be6f-000d3a3bec27
            const bcbs_sharedsavings_billtogroupcurrency = newAttributeSetFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_billtogroupcurrency_id];

            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=89606da3-585e-ee11-be6f-000d3a3bec27
            const bcbs_sharedsavings_captypecode = newAttributeSetFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_captypecode_id];

            // Cap Amount
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=8b606da3-585e-ee11-be6f-000d3a3bec27
            const bcbs_sharedsavings_capamount = newAttributeSetFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_capamount_id];

            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=8d606da3-585e-ee11-be6f-000d3a3bec27
            const bcbs_sharedsavings_partnerplanpercent = newAttributeSetFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_partnerplanpercent_id];

            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=8f606da3-585e-ee11-be6f-000d3a3bec27
            const bcbs_sharedsavings_natokeeppercent = newAttributeSetFieldMap[$att_$fees_and_premiums_$bcbs_sharedsavings_natokeeppercent_id];

            bcbs_sharedsavings_categorycode.values[0].value = {
                "type": "entity",
                "entityType": "bcbs_config_optionsetitem",
                "id": $oset_$shared_savings_category_$other_id,
                "name": "Other"
            };

            bcbs_sharedsavings_sspicomponentcode.values[0].value = {
                "type": "entity",
                "entityType": "bcbs_config_optionsetitem",
                "id": $oset_$shared_savings_ss_pi_component_other_$eligibility_over_payment_retroactive_term_id,
                "name": "Eligibility Over Payment / Retroactive Term"
            }

            delete bcbs_sharedsavings_sspicomponentcode.visibility

            bcbs_sharedsavings_captypecode.values[0].value = {
                "type": "entity",
                "entityType": "bcbs_config_optionsetitem",
                "id": $oset_$shared_savings_cap_type_$per_claim_id,
                "name": "Per Claim"
            }

            if (percent !== undefined)
                bcbs_sharedsavings_billtogrouppercent.values[0].value = {
                    type: 'number',
                    value: percent,
                }

            bcbs_sharedsavings_capamount.values[0].value = {
                type: 'number',
                value: 5000,
            }



            feesAndPremium.attributeSets.push(newAttributeSet)
        }

        const relatedPlans = this.plansByFeesAndPremiumsRefId[feesAndPremium.id];

        const relatedARsForFNP: FAM_IAccountsReceivable[] = []

        if (relatedPlans)
            relatedPlans.forEach(eachPlan => {
                const relatedGroupNumbers = this.groupNumbersByPlanRefId[eachPlan.id] ?? [];
                relatedGroupNumbers.forEach(eachGN => {

                    if (!eachGN.accountsReceivableRef?.id)
                        return;

                    const relatedARs = this.accountsReceivablesById[eachGN.accountsReceivableRef?.id] ?? [];

                    relatedARs.forEach(eachAccountsReceivable => {
                        if (!relatedARsForFNP.includes(eachAccountsReceivable))
                            relatedARsForFNP.push(eachAccountsReceivable);
                    })
                });
            });


        const distinctPercentages: Record<string, string> = {};

        relatedARsForFNP.forEach(eachAR => {
            const fieldMap = ArrayToLookupByPredicate(eachAR.fields, (t) => {
                return t.configRef.id
            });

            // Claim Recovery Percentage (Recovery Fee)
            // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=b6d36d78-b28d-ec11-b400-0022480a93ed
            const bcbs_claimrecoverypercentage = fieldMap[$att_$accounts_receivable_$bcbs_claimrecoverypercentage_id]

            // Enter Percentage of Recovery - Retro Term Claim
            // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=b4d36d78-b28d-ec11-b400-0022480a93ed
            const bcbs_enterpercentageofrecoveryretrotermclaim = fieldMap[$att_$accounts_receivable_$bcbs_enterpercentageofrecoveryretrotermclaim_id]

            const bcbs_claimrecoverypercentage_value = bcbs_claimrecoverypercentage.values?.[0]?.value;
            const bcbs_enterpercentageofrecoveryretrotermclaim_value = bcbs_enterpercentageofrecoveryretrotermclaim.values?.[0]?.value;

            let valueInt: number | null = null;

            // ARRTCR -> accounts receivable retroactive termination claim recoveries
            const valueName = `ARRTCR - ${Acronym(eachAR.name)}`;

            if (bcbs_claimrecoverypercentage_value && bcbs_claimrecoverypercentage_value.type === 'entity') {
                if (bcbs_claimrecoverypercentage_value.id === bcbs_claimrecoverypercentage_30percent.id) {
                    // if "30% of Recoveries" was picked, we will default to 30 %
                    valueInt = 30;

                } else if (bcbs_claimrecoverypercentage_value.id === bcbs_claimrecoverypercentage_other.id) {
                    // if "Other" was picked, we will set to Other's value which is string

                    if (bcbs_enterpercentageofrecoveryretrotermclaim_value?.type === 'string') {
                        if (bcbs_enterpercentageofrecoveryretrotermclaim_value.value && bcbs_enterpercentageofrecoveryretrotermclaim_value.value !== "")
                            valueInt = parseInt(bcbs_enterpercentageofrecoveryretrotermclaim_value.value);
                    }
                }
            }

            if (valueInt !== null)
                if (!distinctPercentages[valueInt]) {
                    addAttributeSet(valueInt, valueName);
                    this.addGeneralNotification = true
                    distinctPercentages[valueInt] = valueName;
                }

        });
        // more than 1 type of percentage, lets warn users

        if (Object.keys(distinctPercentages).length > 1) {
            this.json.notifications!.push({
                id: uuidv4(),
                name: 'retroactive-termination-claims-recoveries-multiple-percentages',
                location: { feesAndPremiumId: feesAndPremium.id },

            });
        }
    }

    async after(json: FAM_IGroupConfigurationData): Promise<void> {

        console.debug("[tran] Sprint24Transformer.after")

        if (this.addGeneralNotification) {

            this.json.notifications!.push({
                id: uuidv4(),
                name: 'retroactive-termination-claims-recoveries-general',
            });
        }

        if (!json.transformers)
            json.transformers = {};

        json.transformers.Sprint24 = true;
    }

}
